import React, { useState, useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import { EnvelopeIcon, ArrowLeftIcon } from "@heroicons/react/24/outline"

const EmailInputDialog = ({ store, initialEmail = "", isActive }) => {
  const texts = TextBundle({ id: "aldoo-store" })
  const [email, setEmail] = useState(initialEmail)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showMagicLink, setShowMagicLink] = useState(false)
  const [verificationCredentials, setVerificationCredentials] = useState(null)
  // Set when the user backs out of the magic-link wait, so the resolving
  // waitForVerification call doesn't flash a failure they didn't cause
  const cancelledRef = useRef(false)

  // awaitAuth polls IsDeviceLogged every second until it is broken. Nothing
  // else stops it, so a dialog that goes away mid-wait would keep the request
  // loop alive for the life of the tab.
  useEffect(() => {
    return () => PlatformAuth.SetBreakAwaitAuth(true)
  }, [])

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError(texts?.emailRequired || "Email is required")
      return false
    }

    if (!validateEmail(email)) {
      setError(texts?.invalidEmail || "Please enter a valid email address")
      return false
    }

    setLoading(true)
    setError("")

    try {
      const result = await store.handleEmailVerification(email)

      // Check if verification is required
      if (result?.requiresVerification) {
        cancelledRef.current = false
        setVerificationCredentials(result.credentials)
        setShowMagicLink(true)
        setLoading(false)

        // Start waiting for verification
        const verified = await store.waitForVerification(result.credentials)

        if (cancelledRef.current) return false

        if (verified) {
          setShowMagicLink(false)
          // Reset button state before proceeding to next step
          PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
            stepId: 'email',
            hideButtons: false
          })
          PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { email } })
          return true
        } else {
          setShowMagicLink(false)
          setError(texts?.verificationFailed || "Verification failed or timed out")
          setLoading(false)
          return false
        }
      } else {
        // No verification required, proceed normally
        setLoading(false)
        PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { email } })
        return true
      }
    } catch (err) {
      setError(err.message || texts?.genericError || "An error occurred")
      setLoading(false)
      return false
    }
  }

  const handleMagicLinkBack = () => {
    // Break the poll loop before dropping the UI, otherwise it keeps hitting
    // the server once a second with nothing left to resolve into
    cancelledRef.current = true
    PlatformAuth.SetBreakAwaitAuth(true)
    setShowMagicLink(false)
    setLoading(false)
    setVerificationCredentials(null)
  }

  // Update button state via PubSub - only when this step is active
  useEffect(() => {
    if (!isActive) {
      return
    }

    // When showing magic link, hide both back and continue buttons
    // User can only use "Made a mistake?" to go back
    if (showMagicLink) {
      PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
        stepId: 'email',
        canContinue: false,
        loading: false,
        hideButtons: true, // Hide both back and continue buttons
        onContinue: handleSubmit,
        onBack: () => PubSub.publish("PURCHASE_FLOW_BACK")
      })
    } else {
      PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
        stepId: 'email',
        canContinue: !loading,
        loading: loading,
        hideButtons: false,
        onContinue: handleSubmit,
        onBack: () => PubSub.publish("PURCHASE_FLOW_BACK")
      })
    }
  }, [email, loading, showMagicLink, isActive])

  // Show magic link dialog when verification is required
  if (showMagicLink && verificationCredentials) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="space-y-6 w-full max-w-md px-6 -mt-16 md:min-h-[400px] md:flex md:flex-col md:justify-center">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="bg-primary_lt/30 rounded-full p-4">
              <EnvelopeIcon className="h-16 w-16 text-primary" />
            </div>

            <Typography variant="h3">
              {texts?.checkYourEmail || "Check Your Email"}
            </Typography>

            <div className="w-full border-t border-bordered my-4" />

            <Typography
              variant="body1"
              className="font-medium"
            >
              {verificationCredentials.email}
            </Typography>

            <Typography variant="txt_dim">
              {texts?.magicLinkSent || "Please verify your email to continue"}
            </Typography>

            <Button
              variant="text_btn_primary"
              onClick={handleMagicLinkBack}
              className="flex items-center gap-2 mt-6"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>{texts?.madeMistake || "Made a mistake?"}</span>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="space-y-6 w-full max-w-md px-6 -mt-16 md:min-h-[400px] md:flex md:flex-col md:justify-center">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="bg-primary_lt/30 rounded-full p-4">
            <EnvelopeIcon className="h-8 w-8 text-primary" />
          </div>

          <Typography variant="h3">
            {texts?.enterYourEmail || "Enter Your Email"}
          </Typography>

          <Typography variant="txt_dim">
            {texts?.emailDialogDescription || "We'll use this to send your receipt and account details"}
          </Typography>
        </div>

        <div className="space-y-2 max-w-md mx-auto w-full">
          <Input
            type="email"
            variant="snap_compact"
            value={email}
            onChange={(e) => {
              const newEmail = e.target.value
              setEmail(newEmail)
              // Clear error when user starts typing
              if (error) {
                setError("")
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleSubmit(e)
              }
            }}
            placeholder={texts?.emailPlaceholder || "Please enter your email"}
            autoFocus
            disabled={loading}
            className={`text-center ${error ? "border-error" : ""}`}
          />

          {error && (
            <Typography
              variant="txt1"
              className="block text-error text-center mt-2"
            >
              {error}
            </Typography>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmailInputDialog
